import { CommanderResponseRef } from '@/components/Commander';
import { BaseInfo } from '@/models/baseInfo';
import { produce } from 'immer';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { APPState } from '../app';
import { ReduxState } from '../redux/reducers';
import { BaseInfoActionTypes, BaseInfoState } from '../redux/reducers/common/base';
import { shallowEqual } from 'react-redux';

export type BaseInfoContextType = [
  BaseInfo,
  (data: Partial<BaseInfo>) => void,
  {
    getSupplierName: (id: string | number) => string;
    hasPermission: (permId: number | number[]) => boolean;
  } & CommanderResponseRef,
];

export const BaseInfoContext = React.createContext<BaseInfoContextType>(null);

export const BaseInfoProvider: React.FC<{
  children: React.ReactNode;
  onChange?: ({ error, loaded, loading, baseInfo }: APPState) => void;
  commanderRef?: React.MutableRefObject<CommanderResponseRef>;
}> = ({ children, onChange, commanderRef }) => {
  const {
    baseInfo: { payload, error, loaded, loading },
  } = useSelector<ReduxState, { baseInfo: BaseInfoState }>(
    state => ({
      baseInfo: state.baseInfo,
    }),
    shallowEqual
  );

  useEffect(() => {
    onChange?.({ error, loaded, loading, baseInfo: payload });
  }, [error, loaded, loading, payload]);

  const dispatch = useDispatch();

  useEffect(() => {
    if (!payload) {
      dispatch({
        type: BaseInfoActionTypes.fetch,
      });
    }
  }, [payload]);

  const updateBaseInfo = useCallback(
    partial => {
      dispatch({
        type: BaseInfoActionTypes.update,
        payload: produce(payload, draft => {
          Object.entries(partial).forEach(([key, value]) => {
            draft[key] = value;
          });
        }),
      });
    },
    [dispatch, payload]
  );

  const helpers = useMemo(() => {
    const getSupplierName = (id: string | number) => {
      if (payload && payload.suppliersMap) {
        if (payload.suppliersMap.hasOwnProperty(`${id}`)) {
          return payload.suppliersMap[id].name;
        }
        return `未知供应商`;
      }
      return 'N/A';
    };
    const hasPermission = (permIds: number | number[]) => {
      if (!Array.isArray(permIds)) {
        permIds = [permIds];
      }
      for (let permId of permIds) {
        if (payload.perms.has(permId)) {
          return true;
        }
      }
      return false;
    };
    return {
      hasPermission,
      getSupplierName,
      showOrderModal: commanderRef.current?.showOrderModal,
      showAppointmentModal: commanderRef.current?.showAppointmentModal,
      showUserModal: commanderRef.current?.showUserModal,
      showBindOrderModal: commanderRef.current?.showBindOrderModal,
      showSelectOperatorModal: commanderRef.current?.showSelectOperatorModal,
      closeAppointmentModal: commanderRef.current?.closeAppointmentModal,
      closeOrderModal: commanderRef.current?.closeOrderModal,
      closeUserModal: commanderRef.current?.closeUserModal,
      closeSelectOperatorModal: commanderRef.current?.closeSelectOperatorModal,
    };
  }, [payload, commanderRef.current]);

  const baseInfoValues = useMemo<BaseInfoContextType>(() => {
    return [payload, updateBaseInfo, helpers];
  }, [payload, updateBaseInfo, helpers]);

  return <BaseInfoContext.Provider value={baseInfoValues}>{children}</BaseInfoContext.Provider>;
};

export const useBaseInfo = () => {
  const ctx = useContext(BaseInfoContext);
  if (!ctx) {
    throw new Error('useBaseInfo must be used within a BaseInfoProvider');
  }
  return ctx;
};
